/**
 * Klaviyo Back in Stock
 *
 * Posts signups to Klaviyo's public Client API endpoint:
 *   POST https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id={publicKey}
 *
 * Klaviyo's built-in Back in Stock subscription automatically removes a
 * subscriber from the notification list once their variant is notified,
 * so no extra cleanup is needed on our end. To re-subscribe, the customer
 * just clicks "Notify Me" again.
 *
 * Docs: https://developers.klaviyo.com/en/reference/create_back_in_stock_subscription
 */
(function () {
  'use strict';

  var KLAVIYO_ENDPOINT = 'https://a.klaviyo.com/client/back-in-stock-subscriptions/';
  var KLAVIYO_REVISION = '2024-10-15';

  function init(root) {
    var dialog = root.querySelector('dialog');
    var formEl = root.querySelector('[data-klaviyo-bis-form]');
    var variantInput = root.querySelector('[data-klaviyo-bis-variant-id]');
    var emailInput = root.querySelector('[data-klaviyo-bis-email]');
    var variantLabel = root.querySelector('[data-klaviyo-bis-variant-label]');
    var successEl = root.querySelector('[data-klaviyo-bis-success]');
    var errorEl = root.querySelector('[data-klaviyo-bis-error]');
    var closeButtons = root.querySelectorAll('[data-klaviyo-bis-close]');
    var submitBtn = root.querySelector('[data-klaviyo-bis-submit]');
    var companyId = root.dataset.companyId;
    var productId = root.dataset.productId;

    if (!dialog || !formEl || !submitBtn || !emailInput || !companyId) {
      if (window.console && console.warn) {
        console.warn('[Klaviyo BIS] init bailed — missing element', {
          dialog: !!dialog,
          formEl: !!formEl,
          submitBtn: !!submitBtn,
          emailInput: !!emailInput,
          companyId: !!companyId
        });
      }
      return;
    }

    // Tracks the submission mode for the currently-open dialog: 'standard' (the
    // existing genuine-sold-out flow that uses Klaviyo's back-in-stock-subscriptions
    // endpoint) or 'reserved' (Phase 2 — subscription-reserved variants, which need
    // a different submit path because Klaviyo sees the variant as in-stock).
    var currentMode = 'standard';

    function resetMessages() {
      if (successEl) successEl.hidden = true;
      if (errorEl) errorEl.hidden = true;
      formEl.classList.remove('klaviyo-bis__form--sent');
    }

    function openModal(detail) {
      resetMessages();
      emailInput.value = '';

      currentMode = (detail && detail.mode) === 'reserved' ? 'reserved' : 'standard';

      if (detail && detail.variantId) {
        variantInput.value = detail.variantId;
      }
      if (variantLabel) {
        var labelParts = [];
        if (detail && detail.productTitle) labelParts.push(detail.productTitle);
        // Skip Shopify's auto-generated "Default Title" when a product has no variants
        if (detail && detail.variantTitle && detail.variantTitle !== 'Default Title') {
          labelParts.push(detail.variantTitle);
        }
        variantLabel.textContent = labelParts.join(' — ');
      }

      if (typeof dialog.showModal === 'function') {
        dialog.showModal();
      } else {
        dialog.setAttribute('open', '');
      }
    }

    function closeModal() {
      if (typeof dialog.close === 'function') {
        dialog.close();
      } else {
        dialog.removeAttribute('open');
      }
    }

    // Listen for global open requests (buy-buttons fires these)
    document.addEventListener('klaviyo-bis:open', function (e) {
      var detail = e.detail || {};
      // Only open the modal that matches this product
      if (detail.productId && String(detail.productId) !== String(productId)) {
        return;
      }
      openModal(detail);
    });

    closeButtons.forEach(function (btn) {
      btn.addEventListener('click', closeModal);
    });

    // Close if clicking on the backdrop
    dialog.addEventListener('click', function (e) {
      if (e.target === dialog) {
        closeModal();
      }
    });

    // Submit on click of the inner button OR Enter key in the email field.
    submitBtn.addEventListener('click', handleSubmit);
    emailInput.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleSubmit();
      }
    });

    function handleSubmit() {
      resetMessages();

      var email = emailInput.value.trim();
      var variantId = variantInput.value;

      // Lightweight email validation (since we removed the native form's required + type checks)
      var emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
      if (!emailValid || !variantId) {
        if (errorEl) errorEl.hidden = false;
        return;
      }

      submitBtn.disabled = true;
      submitBtn.setAttribute('aria-busy', 'true');

      var submitPromise =
        currentMode === 'reserved'
          ? submitReserved(email, variantId)
          : submitStandard(email, variantId);

      submitPromise
        .then(function () {
          if (successEl) successEl.hidden = false;
          formEl.classList.add('klaviyo-bis__form--sent');
        })
        .catch(function (err) {
          if (window.console && console.error) {
            console.error('[Klaviyo BIS]', err);
          }
          if (errorEl) errorEl.hidden = false;
        })
        .finally(function () {
          if (submitBtn) {
            submitBtn.disabled = false;
            submitBtn.removeAttribute('aria-busy');
          }
        });
    }

    // Standard genuine-sold-out flow: POST to Klaviyo's native back-in-stock
    // subscription endpoint. Klaviyo's Shopify integration fires the email
    // automatically when inventory comes back.
    function submitStandard(email, variantId) {
      var payload = {
        data: {
          type: 'back-in-stock-subscription',
          attributes: {
            channels: ['EMAIL'],
            profile: {
              data: {
                type: 'profile',
                attributes: { email: email }
              }
            }
          },
          relationships: {
            variant: {
              data: {
                type: 'catalog-variant',
                // Klaviyo's Shopify integration stores variants as
                // $shopify:::$default:::{variantId}
                id: '$shopify:::$default:::' + variantId
              }
            }
          }
        }
      };

      return fetch(KLAVIYO_ENDPOINT + '?company_id=' + encodeURIComponent(companyId), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          revision: KLAVIYO_REVISION
        },
        body: JSON.stringify(payload)
      }).then(function (response) {
        if (response.status >= 200 && response.status < 300) return;
        return response.text().then(function (body) {
          // Treat "already subscribed" as success too
          if (body && body.toLowerCase().indexOf('already') !== -1) return;
          throw new Error('Klaviyo error ' + response.status + ': ' + body);
        });
      });
    }

    // Phase 2 reserved-variant flow: append the variant_id to the customer's
    // watching_variants profile property using Klaviyo's `meta.patch_properties`
    // block, which is the documented mechanism for atomic array operations
    // (append, unappend, unset) on profile properties.
    //
    // We bypass _learnq.push for this because the legacy SDK only knows the
    // older `$append` operator inside `properties`, which the modern Klaviyo
    // /client/profiles/ endpoint silently stores as a literal property
    // instead of processing as an array operation. Calling /client/profiles/
    // directly with the meta.patch_properties.append structure works.
    //
    // Docs: https://www.klaviyo.com/blog/solution-recipe-25-append-unappend-and-unset-custom-properties-programmatically-with-klaviyo
    function submitReserved(email, variantId) {
      var payload = {
        data: {
          type: 'profile',
          attributes: {
            email: email,
            properties: {}
          },
          meta: {
            patch_properties: {
              append: {
                watching_variants: String(variantId)
              }
            }
          }
        }
      };

      return fetch(
        'https://a.klaviyo.com/client/profiles/?company_id=' + encodeURIComponent(companyId),
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            revision: KLAVIYO_REVISION
          },
          body: JSON.stringify(payload)
        }
      ).then(function (response) {
        if (response.status >= 200 && response.status < 300) return;
        return response.text().then(function (body) {
          throw new Error('Klaviyo error ' + response.status + ': ' + body);
        });
      });
    }
  }

  function bootAll() {
    document.querySelectorAll('klaviyo-back-in-stock').forEach(function (el) {
      if (el.__klaviyoBisBound) return;
      el.__klaviyoBisBound = true;
      init(el);
    });
    bindNotifyButtons();
    bindVariantWatchers();
  }

  // Read variant availability for a given product id from the embedded JSON.
  function getVariantMap(productId) {
    var script = document.querySelector(
      'script[data-product-variants-availability="' + productId + '"]'
    );
    if (!script) return null;
    try {
      return JSON.parse(script.textContent);
    } catch (err) {
      return null;
    }
  }

  // Toggle the add-to-cart wrapper vs the notify-me button based on availability.
  function applyVariantState(form, variantId) {
    var notifyBtn = form.querySelector('[data-notify-me-button]');
    if (!notifyBtn) return;

    var atcWrapper = form.querySelector('[data-atc-wrapper]');
    var productId = notifyBtn.getAttribute('data-product-id');
    var variants = getVariantMap(productId);
    if (!variants) return;

    var variant = variants[String(variantId)];
    if (!variant) return;

    if (variant.available === false) {
      notifyBtn.setAttribute('data-variant-id', String(variantId));
      notifyBtn.setAttribute('data-variant-title', variant.title || '');
      notifyBtn.hidden = false;
      if (atcWrapper) atcWrapper.hidden = true;
    } else {
      notifyBtn.hidden = true;
      if (atcWrapper) atcWrapper.hidden = false;
    }
  }

  function bindNotifyButtons() {
    document.querySelectorAll('[data-notify-me-button]').forEach(function (btn) {
      if (btn.__klaviyoBisBound) return;
      btn.__klaviyoBisBound = true;

      btn.addEventListener('click', function () {
        var detail = {
          productId: btn.getAttribute('data-product-id'),
          productTitle: btn.getAttribute('data-product-title') || '',
          variantId: btn.getAttribute('data-variant-id'),
          variantTitle: btn.getAttribute('data-variant-title') || '',
          // Phase 2: 'reserved' = subscription-reserved variant (append to
          // watching_variants); anything else falls through to the standard
          // genuine-sold-out flow.
          mode: btn.getAttribute('data-bis-mode') || 'standard'
        };
        document.dispatchEvent(new CustomEvent('klaviyo-bis:open', { detail: detail }));
      });
    });
  }

  function bindVariantWatchers() {
    // Each product form has an input[name="id"] that gets its value set
    // when the customer changes variants. Watch both attribute and value
    // changes so we catch all the theme's update paths.
    document.querySelectorAll('form[action*="/cart/add"], form[data-type="add-to-cart-form"]').forEach(function (form) {
      if (form.__klaviyoBisVariantBound) return;
      form.__klaviyoBisVariantBound = true;

      var variantInput = form.querySelector('input[name="id"]');
      if (!variantInput) return;

      // Sync on initial load (in case the form server-rendered for a sold-out variant)
      applyVariantState(form, variantInput.value);

      // Listen for input/change events (most pickers dispatch these)
      variantInput.addEventListener('change', function () {
        applyVariantState(form, variantInput.value);
      });
      variantInput.addEventListener('input', function () {
        applyVariantState(form, variantInput.value);
      });

      // Also watch the value attribute (some pickers set it via setAttribute)
      var observer = new MutationObserver(function () {
        applyVariantState(form, variantInput.value);
      });
      observer.observe(variantInput, { attributes: true, attributeFilter: ['value'] });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootAll);
  } else {
    bootAll();
  }

  // Re-bind if Shopify section rendering swaps markup
  document.addEventListener('shopify:section:load', bootAll);
})();
